import React, { useEffect, useState } from 'react';
import {
  Box, Card, Typography, Button, Stack, Chip, Table, TableBody, TableCell,
  TableHead, TableRow, IconButton, Tooltip, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, FormControl, InputLabel, Select, MenuItem,
  Switch, FormControlLabel, CircularProgress, Alert
} from '@mui/material';
import {
  Schedule as ScheduleIcon, Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon,
  PlayArrow as RunIcon, History as HistoryIcon, Refresh as RefreshIcon,
  Close as CloseIcon, CheckCircle as SuccessIcon, ErrorOutline as FailIcon,
  RadioButtonChecked as RunningIcon
} from '@mui/icons-material';
import { useLanguage } from '../../../contexts/LanguageContext';
import config from '../../../config';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useConfirm } from '../../../hooks/useConfirm';

interface BackupSchedule {
  scheduleId: number;
  name: string;
  backupType: 'main' | 'fiscal' | 'all';
  databaseName?: string | null;
  frequencyType: 'Hourly' | 'Daily' | 'Weekly';
  intervalHours?: number | null;
  timeOfDay?: string | null;  // "HH:mm"
  dayOfWeek?: number | null;
  retentionDays: number;
  runIfMissed: boolean;
  notifyOnFailure: boolean;
  notifyOnSuccess: boolean;
  isEnabled: boolean;
  lastRunDate?: string | null;
  nextRunDate?: string | null;
  createdDate: string;
}

interface BackupHistoryEntry {
  backupHistoryId: number;
  scheduleId?: number | null;
  databaseName: string;
  fileName: string;
  fileSizeMB?: number | null;
  status: 'Running' | 'Success' | 'Failed';
  triggerType: 'Manual' | 'Scheduled' | 'Startup';
  startedAt: string;
  completedAt?: string | null;
  durationSeconds?: number | null;
  errorMessage?: string | null;
}

interface FiscalDb {
  databaseName: string;
  periodName: string;
}

const t = {
  ar: {
    title: 'الجدولة التلقائية للنسخ الاحتياطية',
    subtitle: 'نسخ احتياطية تلقائية حسب جدول زمني مرن',
    addSchedule: 'إضافة جدولة',
    history: 'سجل التشغيلات',
    refresh: 'تحديث',
    name: 'الاسم',
    target: 'القاعدة',
    frequency: 'التكرار',
    time: 'الوقت',
    retention: 'الاحتفاظ',
    enabled: 'مفعّلة',
    lastRun: 'آخر تشغيل',
    nextRun: 'التشغيل التالي',
    actions: 'الإجراءات',
    edit: 'تعديل',
    delete: 'حذف',
    runNow: 'تشغيل الآن',
    toggle: 'تشغيل/إيقاف',
    noSchedules: 'لا توجد جدولات. أنشئ واحدة لبدء النسخ التلقائي.',
    days: 'يوم',
    hours: 'ساعة',
    everyN: 'كل',
    daily: 'يومي',
    hourly: 'كل ساعة',
    weekly: 'أسبوعي',
    backupMain: 'الرئيسية',
    backupFiscal: 'سنة محددة',
    backupAll: 'الكل',
    on: 'مفعّلة',
    off: 'متوقفة',
    // Dialog
    editTitle: 'تعديل الجدولة',
    addTitle: 'إضافة جدولة جديدة',
    save: 'حفظ',
    cancel: 'إلغاء',
    nameLabel: 'اسم الجدولة',
    backupTypeLabel: 'نوع النسخ',
    fiscalDbLabel: 'السنة المالية',
    frequencyLabel: 'نوع التكرار',
    intervalLabel: 'الفاصل بالساعات',
    timeLabel: 'الوقت (24 ساعة)',
    dayLabel: 'يوم الأسبوع',
    retentionLabel: 'الاحتفاظ (أيام)',
    runIfMissedLabel: 'تشغيل لو فاتها',
    notifyFailLabel: 'تنبيه عند الفشل',
    notifySuccessLabel: 'تنبيه عند النجاح',
    isEnabledLabel: 'مفعّلة',
    confirmDelete: 'حذف هذه الجدولة؟',
    confirmRunNow: 'تشغيل النسخ الاحتياطية الآن لهذه الجدولة؟',
    // History
    historyTitle: 'سجل النسخ الاحتياطية',
    fileName: 'الملف',
    db: 'القاعدة',
    size: 'الحجم',
    status: 'الحالة',
    duration: 'المدة',
    trigger: 'النوع',
    startedAt: 'البداية',
    error: 'الخطأ',
    triggerManual: 'يدوي',
    triggerScheduled: 'مجدول',
    triggerStartup: 'بدء',
    statusSuccess: 'نجح',
    statusFailed: 'فشل',
    statusRunning: 'يعمل',
    noHistory: 'لا يوجد سجل',
    sunday: 'الأحد', monday: 'الإثنين', tuesday: 'الثلاثاء', wednesday: 'الأربعاء',
    thursday: 'الخميس', friday: 'الجمعة', saturday: 'السبت',
  },
  en: {
    title: 'Scheduled Automatic Backups',
    subtitle: 'Automatic backups on a flexible schedule',
    addSchedule: 'Add Schedule',
    history: 'History',
    refresh: 'Refresh',
    name: 'Name', target: 'Target', frequency: 'Frequency', time: 'Time',
    retention: 'Retention', enabled: 'Enabled', lastRun: 'Last Run',
    nextRun: 'Next Run', actions: 'Actions',
    edit: 'Edit', delete: 'Delete', runNow: 'Run Now', toggle: 'Toggle',
    noSchedules: 'No schedules. Create one to start automatic backups.',
    days: 'day(s)', hours: 'hour(s)', everyN: 'every',
    daily: 'Daily', hourly: 'Hourly', weekly: 'Weekly',
    backupMain: 'Main', backupFiscal: 'Specific Year', backupAll: 'All',
    on: 'ON', off: 'OFF',
    editTitle: 'Edit Schedule', addTitle: 'New Schedule',
    save: 'Save', cancel: 'Cancel',
    nameLabel: 'Schedule Name', backupTypeLabel: 'Backup Type',
    fiscalDbLabel: 'Fiscal Year', frequencyLabel: 'Frequency',
    intervalLabel: 'Interval (hours)', timeLabel: 'Time (24h)',
    dayLabel: 'Day of Week', retentionLabel: 'Retention (days)',
    runIfMissedLabel: 'Run if missed', notifyFailLabel: 'Notify on failure',
    notifySuccessLabel: 'Notify on success', isEnabledLabel: 'Enabled',
    confirmDelete: 'Delete this schedule?',
    confirmRunNow: 'Run backups now for this schedule?',
    historyTitle: 'Backup History',
    fileName: 'File', db: 'DB', size: 'Size', status: 'Status',
    duration: 'Duration', trigger: 'Trigger', startedAt: 'Started', error: 'Error',
    triggerManual: 'Manual', triggerScheduled: 'Scheduled', triggerStartup: 'Startup',
    statusSuccess: 'Success', statusFailed: 'Failed', statusRunning: 'Running',
    noHistory: 'No history',
    sunday: 'Sun', monday: 'Mon', tuesday: 'Tue', wednesday: 'Wed',
    thursday: 'Thu', friday: 'Fri', saturday: 'Sat',
  }
};

const emptyForm = {
  name: '',
  backupType: 'all' as 'main' | 'fiscal' | 'all',
  databaseName: '',
  frequencyType: 'Daily' as 'Hourly' | 'Daily' | 'Weekly',
  intervalHours: 1,
  timeOfDay: '02:00',
  dayOfWeek: 0,
  retentionDays: 30,
  runIfMissed: true,
  notifyOnFailure: true,
  notifyOnSuccess: false,
  isEnabled: true,
};

interface Props {
  fiscalDatabases: FiscalDb[];
  onRefreshBackups?: () => void;
}

export default function BackupSchedulesSection({ fiscalDatabases, onRefreshBackups }: Props) {
  const { language } = useLanguage();
  const isRTL = language === 'ar';
  const tt = t[language as 'ar' | 'en'];
  const confirm = useConfirm();

  const [schedules, setSchedules] = useState<BackupSchedule[]>([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState<BackupHistoryEntry[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyScheduleId, setHistoryScheduleId] = useState<number | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);

  const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${config.API_BASE_URL}/BackupSchedules`, { headers: headers() });
      setSchedules(data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const openAdd = () => {
    setEditingId(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (s: BackupSchedule) => {
    setEditingId(s.scheduleId);
    setForm({
      name: s.name,
      backupType: s.backupType,
      databaseName: s.databaseName || '',
      frequencyType: s.frequencyType,
      intervalHours: s.intervalHours ?? 1,
      timeOfDay: s.timeOfDay || '02:00',
      dayOfWeek: s.dayOfWeek ?? 0,
      retentionDays: s.retentionDays,
      runIfMissed: s.runIfMissed,
      notifyOnFailure: s.notifyOnFailure,
      notifyOnSuccess: s.notifyOnSuccess,
      isEnabled: s.isEnabled,
    });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) {
      toast.warning(isRTL ? 'الاسم مطلوب' : 'Name is required');
      return;
    }
    if (form.backupType === 'fiscal' && !form.databaseName) {
      toast.warning(isRTL ? 'اختر السنة المالية' : 'Select a fiscal year');
      return;
    }
    setSaving(true);
    try {
      const body = {
        ...form,
        databaseName: form.backupType === 'fiscal' ? form.databaseName : null,
        intervalHours: form.frequencyType === 'Hourly' ? form.intervalHours : null,
        timeOfDay: form.frequencyType !== 'Hourly' ? form.timeOfDay : null,
        dayOfWeek: form.frequencyType === 'Weekly' ? form.dayOfWeek : null,
      };
      if (editingId) {
        await axios.put(`${config.API_BASE_URL}/BackupSchedules/${editingId}`, body, { headers: headers() });
        toast.success(isRTL ? 'تم التحديث' : 'Updated');
      } else {
        await axios.post(`${config.API_BASE_URL}/BackupSchedules`, body, { headers: headers() });
        toast.success(isRTL ? 'تم الإنشاء' : 'Created');
      }
      setDialogOpen(false);
      load();
    } catch (e: any) {
      toast.error(e.response?.data?.message || (isRTL ? 'فشل الحفظ' : 'Save failed'));
    } finally { setSaving(false); }
  };

  const toggle = async (id: number) => {
    setBusyId(id);
    try {
      await axios.put(`${config.API_BASE_URL}/BackupSchedules/${id}/toggle`, {}, { headers: headers() });
      load();
    } catch (e) { console.error(e); }
    finally { setBusyId(null); }
  };

  const runNow = async (id: number) => {
    const ok = await confirm({ message: tt.confirmRunNow, severity: 'info' });
    if (!ok) return;
    setBusyId(id);
    try {
      const { data } = await axios.post(`${config.API_BASE_URL}/BackupSchedules/${id}/run-now`, {}, { headers: headers() });
      if (data.failureCount > 0) {
        toast.warning(isRTL
          ? `اكتمل: ${data.successCount} نجاح، ${data.failureCount} فشل`
          : `Completed: ${data.successCount} success, ${data.failureCount} failed`);
      } else {
        toast.success(isRTL ? `تم بنجاح: ${data.successCount} قاعدة` : `Success: ${data.successCount} databases`);
      }
      load();
      onRefreshBackups?.();
    } catch (e: any) {
      toast.error(e.response?.data?.message || (isRTL ? 'فشل التشغيل' : 'Run failed'));
    } finally { setBusyId(null); }
  };

  const del = async (id: number) => {
    const ok = await confirm({ message: tt.confirmDelete, severity: 'error' });
    if (!ok) return;
    setBusyId(id);
    try {
      await axios.delete(`${config.API_BASE_URL}/BackupSchedules/${id}`, { headers: headers() });
      load();
    } catch (e) { console.error(e); }
    finally { setBusyId(null); }
  };

  const openHistory = async (scheduleId?: number) => {
    setHistoryScheduleId(scheduleId ?? null);
    setHistoryOpen(true);
    setHistoryLoading(true);
    try {
      const url = scheduleId
        ? `${config.API_BASE_URL}/BackupSchedules/history?take=100&scheduleId=${scheduleId}`
        : `${config.API_BASE_URL}/BackupSchedules/history?take=100`;
      const { data } = await axios.get(url, { headers: headers() });
      setHistory(data);
    } catch (e) { console.error(e); }
    finally { setHistoryLoading(false); }
  };

  const fmtDateTime = (iso?: string | null) =>
    iso ? new Date(iso).toLocaleString(isRTL ? 'ar-KW' : 'en-GB',
      { dateStyle: 'short', timeStyle: 'short', timeZone: 'Asia/Kuwait' }) : '-';

  const frequencyText = (s: BackupSchedule) => {
    if (s.frequencyType === 'Hourly') return `${tt.everyN} ${s.intervalHours} ${tt.hours}`;
    if (s.frequencyType === 'Weekly') {
      const dayNames = [tt.sunday, tt.monday, tt.tuesday, tt.wednesday, tt.thursday, tt.friday, tt.saturday];
      return `${tt.weekly} (${dayNames[s.dayOfWeek ?? 0]})`;
    }
    return tt.daily;
  };

  const targetText = (s: BackupSchedule) => {
    if (s.backupType === 'main') return tt.backupMain;
    if (s.backupType === 'all') return tt.backupAll;
    return s.databaseName || '-';
  };

  const statusChip = (status: string) => {
    if (status === 'Success') return <Chip icon={<SuccessIcon />} label={tt.statusSuccess} size="small" sx={{ bgcolor: '#43a047', color: '#fff' }} />;
    if (status === 'Failed') return <Chip icon={<FailIcon />} label={tt.statusFailed} size="small" sx={{ bgcolor: '#e53935', color: '#fff' }} />;
    return <Chip icon={<RunningIcon />} label={tt.statusRunning} size="small" sx={{ bgcolor: '#fb8c00', color: '#fff' }} />;
  };

  return (
    <Card sx={{ borderRadius: 3, p: 3, mt: 3 }} dir={isRTL ? 'rtl' : 'ltr'}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
        <Box display="flex" alignItems="center" gap={1.5}>
          <Box sx={{ width: 48, height: 48, borderRadius: 2, bgcolor: 'rgba(33,150,243,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <ScheduleIcon sx={{ color: '#2196f3', fontSize: 28 }} />
          </Box>
          <Box>
            <Typography variant="h6" fontWeight="bold">{tt.title}</Typography>
            <Typography variant="caption" color="text.secondary">{tt.subtitle}</Typography>
          </Box>
        </Box>
        <Stack direction="row" spacing={1}>
          <Tooltip title={tt.refresh}><IconButton onClick={load}><RefreshIcon /></IconButton></Tooltip>
          <Button variant="outlined" startIcon={<HistoryIcon />} onClick={() => openHistory()}>{tt.history}</Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openAdd}>{tt.addSchedule}</Button>
        </Stack>
      </Stack>

      {loading ? (
        <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
      ) : schedules.length === 0 ? (
        <Alert severity="info">{tt.noSchedules}</Alert>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'rgba(33,150,243,0.05)' }}>
              {[tt.name, tt.target, tt.frequency, tt.time, tt.retention, tt.enabled, tt.lastRun, tt.nextRun, tt.actions].map(h => (
                <TableCell key={h} sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {schedules.map(s => (
              <TableRow key={s.scheduleId} hover sx={{ opacity: s.isEnabled ? 1 : 0.55 }}>
                <TableCell>
                  <Typography variant="body2" fontWeight={600}>{s.name}</Typography>
                </TableCell>
                <TableCell>
                  <Chip label={targetText(s)} size="small" sx={{
                    bgcolor: s.backupType === 'all' ? '#9c27b0' : s.backupType === 'main' ? '#2196f3' : '#ff9800',
                    color: '#fff', fontSize: '0.7rem', fontWeight: 600
                  }} />
                </TableCell>
                <TableCell><Typography variant="caption">{frequencyText(s)}</Typography></TableCell>
                <TableCell><Typography variant="caption" fontFamily="monospace">{s.frequencyType === 'Hourly' ? '-' : (s.timeOfDay || '-')}</Typography></TableCell>
                <TableCell><Typography variant="caption">{s.retentionDays} {tt.days}</Typography></TableCell>
                <TableCell>
                  <Switch
                    checked={s.isEnabled}
                    disabled={busyId === s.scheduleId}
                    onChange={() => toggle(s.scheduleId)}
                    size="small"
                  />
                </TableCell>
                <TableCell><Typography variant="caption">{fmtDateTime(s.lastRunDate)}</Typography></TableCell>
                <TableCell><Typography variant="caption" sx={{ color: '#1976d2', fontWeight: 600 }}>{fmtDateTime(s.nextRunDate)}</Typography></TableCell>
                <TableCell>
                  <Stack direction="row" spacing={0.5}>
                    <Tooltip title={tt.runNow}><IconButton size="small" onClick={() => runNow(s.scheduleId)} disabled={busyId === s.scheduleId} sx={{ color: '#43a047' }}><RunIcon fontSize="small" /></IconButton></Tooltip>
                    <Tooltip title={tt.history}><IconButton size="small" onClick={() => openHistory(s.scheduleId)} sx={{ color: '#1976d2' }}><HistoryIcon fontSize="small" /></IconButton></Tooltip>
                    <Tooltip title={tt.edit}><IconButton size="small" onClick={() => openEdit(s)} sx={{ color: '#fb8c00' }}><EditIcon fontSize="small" /></IconButton></Tooltip>
                    <Tooltip title={tt.delete}><IconButton size="small" onClick={() => del(s.scheduleId)} disabled={busyId === s.scheduleId} sx={{ color: '#e53935' }}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {/* ── Add/Edit Dialog ── */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth dir={isRTL ? 'rtl' : 'ltr'}>
        <DialogTitle sx={{ background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)', color: '#fff' }}>
          {editingId ? tt.editTitle : tt.addTitle}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Stack spacing={2}>
            <TextField label={tt.nameLabel} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} fullWidth size="small" />

            <FormControl fullWidth size="small">
              <InputLabel>{tt.backupTypeLabel}</InputLabel>
              <Select value={form.backupType} label={tt.backupTypeLabel} onChange={e => setForm({ ...form, backupType: e.target.value as any })}>
                <MenuItem value="main">{tt.backupMain}</MenuItem>
                <MenuItem value="fiscal">{tt.backupFiscal}</MenuItem>
                <MenuItem value="all">{tt.backupAll}</MenuItem>
              </Select>
            </FormControl>

            {form.backupType === 'fiscal' && (
              <FormControl fullWidth size="small">
                <InputLabel>{tt.fiscalDbLabel}</InputLabel>
                <Select value={form.databaseName} label={tt.fiscalDbLabel} onChange={e => setForm({ ...form, databaseName: e.target.value })}>
                  {fiscalDatabases.map(d => (
                    <MenuItem key={d.databaseName} value={d.databaseName}>{d.periodName} — {d.databaseName}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <FormControl fullWidth size="small">
              <InputLabel>{tt.frequencyLabel}</InputLabel>
              <Select value={form.frequencyType} label={tt.frequencyLabel} onChange={e => setForm({ ...form, frequencyType: e.target.value as any })}>
                <MenuItem value="Hourly">{tt.hourly}</MenuItem>
                <MenuItem value="Daily">{tt.daily}</MenuItem>
                <MenuItem value="Weekly">{tt.weekly}</MenuItem>
              </Select>
            </FormControl>

            {form.frequencyType === 'Hourly' && (
              <TextField label={tt.intervalLabel} type="number" inputProps={{ min: 1, max: 24 }} value={form.intervalHours}
                onChange={e => setForm({ ...form, intervalHours: parseInt(e.target.value) || 1 })} fullWidth size="small" />
            )}

            {form.frequencyType !== 'Hourly' && (
              <TextField label={tt.timeLabel} type="time" value={form.timeOfDay}
                onChange={e => setForm({ ...form, timeOfDay: e.target.value })} fullWidth size="small"
                InputLabelProps={{ shrink: true }} />
            )}

            {form.frequencyType === 'Weekly' && (
              <FormControl fullWidth size="small">
                <InputLabel>{tt.dayLabel}</InputLabel>
                <Select value={form.dayOfWeek} label={tt.dayLabel} onChange={e => setForm({ ...form, dayOfWeek: Number(e.target.value) })}>
                  <MenuItem value={0}>{tt.sunday}</MenuItem>
                  <MenuItem value={1}>{tt.monday}</MenuItem>
                  <MenuItem value={2}>{tt.tuesday}</MenuItem>
                  <MenuItem value={3}>{tt.wednesday}</MenuItem>
                  <MenuItem value={4}>{tt.thursday}</MenuItem>
                  <MenuItem value={5}>{tt.friday}</MenuItem>
                  <MenuItem value={6}>{tt.saturday}</MenuItem>
                </Select>
              </FormControl>
            )}

            <TextField label={tt.retentionLabel} type="number" inputProps={{ min: 1, max: 365 }} value={form.retentionDays}
              onChange={e => setForm({ ...form, retentionDays: parseInt(e.target.value) || 30 })} fullWidth size="small" />

            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
              <FormControlLabel control={<Switch checked={form.runIfMissed} onChange={e => setForm({ ...form, runIfMissed: e.target.checked })} />} label={tt.runIfMissedLabel} />
              <FormControlLabel control={<Switch checked={form.isEnabled} onChange={e => setForm({ ...form, isEnabled: e.target.checked })} />} label={tt.isEnabledLabel} />
              <FormControlLabel control={<Switch checked={form.notifyOnFailure} onChange={e => setForm({ ...form, notifyOnFailure: e.target.checked })} />} label={tt.notifyFailLabel} />
              <FormControlLabel control={<Switch checked={form.notifyOnSuccess} onChange={e => setForm({ ...form, notifyOnSuccess: e.target.checked })} />} label={tt.notifySuccessLabel} />
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogOpen(false)}>{tt.cancel}</Button>
          <Button variant="contained" onClick={save} disabled={saving}>{saving ? <CircularProgress size={20} /> : tt.save}</Button>
        </DialogActions>
      </Dialog>

      {/* ── History Dialog ── */}
      <Dialog open={historyOpen} onClose={() => setHistoryOpen(false)} maxWidth="lg" fullWidth dir={isRTL ? 'rtl' : 'ltr'}>
        <DialogTitle sx={{ background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)', color: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box display="flex" alignItems="center" gap={1}>
            <HistoryIcon />
            <Typography fontWeight="bold">{tt.historyTitle}{historyScheduleId ? ` (#${historyScheduleId})` : ''}</Typography>
          </Box>
          <IconButton size="small" onClick={() => setHistoryOpen(false)} sx={{ color: '#fff' }}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 2 }}>
          {historyLoading ? (
            <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
          ) : history.length === 0 ? (
            <Alert severity="info">{tt.noHistory}</Alert>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  {[tt.startedAt, tt.db, tt.fileName, tt.size, tt.duration, tt.trigger, tt.status, tt.error].map(h => (
                    <TableCell key={h} sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {history.map(h => (
                  <TableRow key={h.backupHistoryId} hover>
                    <TableCell><Typography variant="caption">{fmtDateTime(h.startedAt)}</Typography></TableCell>
                    <TableCell><Chip label={h.databaseName} size="small" sx={{ fontSize: '0.65rem' }} /></TableCell>
                    <TableCell><Typography variant="caption" fontFamily="monospace" sx={{ fontSize: '0.7rem' }}>{h.fileName}</Typography></TableCell>
                    <TableCell><Typography variant="caption">{h.fileSizeMB != null ? `${h.fileSizeMB} MB` : '-'}</Typography></TableCell>
                    <TableCell><Typography variant="caption">{h.durationSeconds != null ? `${h.durationSeconds}s` : '-'}</Typography></TableCell>
                    <TableCell>
                      <Chip label={h.triggerType === 'Manual' ? tt.triggerManual : h.triggerType === 'Scheduled' ? tt.triggerScheduled : tt.triggerStartup}
                        size="small" variant="outlined" sx={{ fontSize: '0.65rem' }} />
                    </TableCell>
                    <TableCell>{statusChip(h.status)}</TableCell>
                    <TableCell>
                      {h.errorMessage ? (
                        <Tooltip title={h.errorMessage}>
                          <Typography variant="caption" color="error" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>
                            {h.errorMessage}
                          </Typography>
                        </Tooltip>
                      ) : '-'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}
